import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import {
  activePreset,
  formatShortDate,
  isoDate,
  PERIOD_PRESET_NAME_ID,
  PeriodChoice,
  periodChoiceOf,
  periodNeedsYear,
  presetRange,
  rangeTextOf,
} from '../../components/novarium/periodRange'
import { PeriodDateField } from '../../components/novarium/PeriodField'
import { fromDateToString } from '../../utils/utils'
import { parseIsoDate } from '../ServicesStatistics/statisticsUtils'
import {
  UrgencyOptionsDTO,
  UrgencySummaryDTO,
  UrgencySummaryRequestDTO,
} from '../../dto/urgencySummary/UrgencySummaryDTO'
import {
  errorMessageOf,
  getUrgencyOptions,
  getUrgencySummary,
  isAccessDenied,
  isRequestCanceled,
} from '../../services/UrgencySummaryController'

/** `ready`: hay un resultado (puede estar actualizándose); `denied`: el perfil no tiene acceso a la pantalla. */
export type SummaryStatus = 'idle' | 'loading' | 'ready' | 'error' | 'denied'

/** Los filtros que se eligen en un combo (todos opcionales: `null` es «Todos»). */
export type UrgencyFilterField = 'templateId' | 'financierId' | 'priorityId'

/** Lo que hay en los filtros de la pantalla; las fechas pueden estar vacías mientras se escriben. */
export interface UrgencyFiltersState {
  from: Date | null
  to: Date | null
  templateId: number | null
  financierId: number | null
  priorityId: number | null
}

export type UrgencyDateErrors = Partial<Record<PeriodDateField, 'required' | 'order'>>

/** El período que había antes de «Ver solo este día» y el rango al que se pasó (`yyyy-MM-dd`). */
interface RangeOrigin {
  previousFrom: Date | null
  previousTo: Date | null
  /** El período anterior era «Personalizado». */
  custom: boolean
  /** Cómo se llamaba el período anterior («Este mes», «1/9 al 30/9»), para «Volver a …». */
  label: string
  from: string
  to: string
  /** Si «Ver solo esta guardia y este día» cambió la guardia: la que había antes (null = todas) y la que se puso. */
  template?: { previous: number | null; applied: number }
}

/** Cuánto se espera antes de consultar cuando los combos cambian seguido. */
const DEBOUNCE_MS = 300

const startOfDay = (date: Date): number => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

function initialFilters(today: Date): UrgencyFiltersState {
  // Del día 1 del mes a hoy: «Mes actual»
  const { from, to } = presetRange('thisMonth', today)
  return { from, to, templateId: null, financierId: null, priorityId: null }
}

function validate(filters: UrgencyFiltersState): UrgencyDateErrors {
  const errors: UrgencyDateErrors = {}
  if (!filters.from) errors.from = 'required'
  if (!filters.to) errors.to = 'required'
  else if (filters.from && startOfDay(filters.from) > startOfDay(filters.to)) errors.to = 'order'
  return errors
}

const sameErrors = (a: UrgencyDateErrors, b: UrgencyDateErrors): boolean => a.from === b.from && a.to === b.to

/** Los filtros ya validados (con las dos fechas) como el pedido al backend. */
export function toSummaryRequest(filters: UrgencyFiltersState): UrgencySummaryRequestDTO | null {
  if (!filters.from || !filters.to) return null
  return {
    from: fromDateToString(filters.from),
    to: fromDateToString(filters.to),
    templateId: filters.templateId,
    financierId: filters.financierId,
    priorityId: filters.priorityId,
  }
}

const sameRequest = (a: UrgencySummaryRequestDTO, b: UrgencySummaryRequestDTO): boolean =>
  a.from === b.from && a.to === b.to && a.templateId === b.templateId && a.financierId === b.financierId
  && a.priorityId === b.priorityId

/**
 * Datos del resumen. Vive en el contenedor de la ruta (no en la pantalla) para que, al volver del detalle, se
 * conserven los filtros y el resultado. Los filtros se aplican al instante, sin «Consultar»: el período y las fechas
 * consultan ya y los combos después de `DEBOUNCE_MS` (pueden cambiar seguidos). Cada consulta cancela la anterior, así
 * que una respuesta vieja nunca pisa a una nueva. Las opciones de los combos se piden una vez, con la primera consulta.
 */
export default function useUrgencySummaryData() {
  const intl = useIntl()
  const today = useMemo(() => new Date(), [])

  const [filters, setFilters] = useState<UrgencyFiltersState>(() => initialFilters(today))
  const filtersRef = useRef(filters)
  const [errors, setErrors] = useState<UrgencyDateErrors>({})
  /** Eligió «Personalizado» en «Período»: las fechas se muestran aunque coincidan con un atajo. */
  const [customPeriod, setCustomPeriod] = useState(false)
  const [options, setOptions] = useState<UrgencyOptionsDTO | null>(null)
  const [summary, setSummary] = useState<UrgencySummaryDTO | null>(null)
  /** Pedido de los datos que se están mostrando. */
  const [applied, setApplied] = useState<UrgencySummaryRequestDTO | null>(null)
  const [status, setStatus] = useState<SummaryStatus>('idle')
  /** Texto del error de la última consulta: el del backend si es de negocio, si no el genérico. */
  const [errorMessage, setErrorMessage] = useState('')
  /** Hay un cambio de filtros esperando el debounce: los datos que se ven ya no son los de los filtros. */
  const [pending, setPending] = useState(false)
  /** El período de antes de «Ver solo este día», para ofrecer «Volver a …». */
  const [rangeOrigin, setRangeOrigin] = useState<RangeOrigin | null>(null)

  const controllerRef = useRef<AbortController | null>(null)
  const debounceRef = useRef<number | undefined>(undefined)
  /** Último pedido que se hizo (aunque siga en vuelo): un cambio que lo repite no consulta de nuevo. */
  const requestedRef = useRef<UrgencySummaryRequestDTO | null>(null)
  const optionsLoadedRef = useRef(false)

  const run = useCallback(async (request: UrgencySummaryRequestDTO) => {
    controllerRef.current?.abort()
    const controller = new AbortController()
    controllerRef.current = controller
    requestedRef.current = request
    setPending(false)
    setStatus('loading')

    try {
      const [loadedOptions, result] = await Promise.all([
        optionsLoadedRef.current ? Promise.resolve(null) : getUrgencyOptions(controller.signal),
        getUrgencySummary(request, controller.signal),
      ])
      if (controller.signal.aborted) return
      if (loadedOptions) {
        optionsLoadedRef.current = true
        setOptions(loadedOptions)
      }
      setSummary(result)
      setApplied(request)
      setStatus('ready')
    } catch (error: unknown) {
      if (controller.signal.aborted || isRequestCanceled(error)) return
      // Un pedido fallido no se recuerda: repetir el mismo filtro lo vuelve a intentar
      requestedRef.current = null
      if (isAccessDenied(error)) {
        setStatus('denied')
        return
      }
      setErrorMessage(errorMessageOf(error, intl.formatMessage({ id: 'UrgencySummaryQueryError' })))
      setStatus('error')
    }
  }, [intl])

  // Al desmontar el contenedor se cancela lo que esté en vuelo
  useEffect(() => () => {
    controllerRef.current?.abort()
    window.clearTimeout(debounceRef.current)
  }, [])

  /** Primera consulta, con los filtros por defecto. No hace nada si ya arrancó. */
  const ensureLoaded = useCallback(() => {
    if (requestedRef.current !== null || status !== 'idle') return
    const request = toSummaryRequest(filtersRef.current)
    if (request) void run(request)
  }, [run, status])

  /**
   * Único camino a la consulta: valida las fechas (el error sale en el campo y no se consulta) y, si cambió algo, consulta
   * ya (`immediate`: período, fechas y chips) o después de `DEBOUNCE_MS` (los combos). Mientras espera, `pending` marca los
   * datos que se ven como viejos.
   */
  const applyFilters = useCallback((next: UrgencyFiltersState, immediate: boolean) => {
    window.clearTimeout(debounceRef.current)
    const validationErrors = validate(next)
    // Sin cambios no se vuelve a renderizar: un renderizado de más puede comerse el click sobre el calendario
    setErrors((current) => (sameErrors(current, validationErrors) ? current : validationErrors))
    const request = Object.keys(validationErrors).length > 0 ? null : toSummaryRequest(next)
    if (!request || (requestedRef.current !== null && sameRequest(request, requestedRef.current))) {
      setPending(false)
      return
    }
    if (immediate) {
      void run(request)
    } else {
      setPending(true)
      debounceRef.current = window.setTimeout(() => void run(request), DEBOUNCE_MS)
    }
  }, [run])

  const setFiltersNow = useCallback((next: UrgencyFiltersState) => {
    filtersRef.current = next
    setFilters(next)
  }, [])

  /** Un combo (guardia, financiador o código): se guarda y se consulta con debounce. */
  const changeFilter = useCallback((field: UrgencyFilterField, value: number | null) => {
    const next = { ...filtersRef.current, [field]: value }
    setFiltersNow(next)
    applyFilters(next, false)
  }, [setFiltersNow, applyFilters])

  /** Un chip o «Quitar todos»: consulta ya. */
  const patchFilters = useCallback((patch: Partial<Pick<UrgencyFiltersState, UrgencyFilterField>>) => {
    const next = { ...filtersRef.current, ...patch }
    setFiltersNow(next)
    applyFilters(next, true)
  }, [setFiltersNow, applyFilters])

  /** Una fecha completa y válida (o vacía, con «Limpiar»): se guarda y se aplica ya. */
  const changeDate = useCallback((field: PeriodDateField, value: Date | null) => {
    const next = { ...filtersRef.current, [field]: value }
    setFiltersNow(next)
    applyFilters(next, true)
  }, [setFiltersNow, applyFilters])

  /** Al salir de los campos de fecha o con Enter: se vuelve a validar y aplicar lo que haya (sin cambios, no hace nada). */
  const commitDates = useCallback(() => applyFilters(filtersRef.current, true), [applyFilters])

  /** «Período»: un atajo aplica su rango ya; «Personalizado» solo muestra las fechas (conservan el rango actual). */
  const changePeriod = useCallback((choice: PeriodChoice) => {
    setCustomPeriod(choice === 'custom')
    if (choice === 'custom') return
    const { from, to } = presetRange(choice, today)
    const next = { ...filtersRef.current, from, to }
    setFiltersNow(next)
    applyFilters(next, true)
  }, [today, setFiltersNow, applyFilters])

  /**
   * «Ver solo este día» (o un rango): pasa a ser el período de la pantalla, como un período personalizado. Con `templateId`
   * («Ver solo esta guardia y este día») también pasa a ser el filtro de guardia, en la misma consulta. Si ya era ese no
   * cambia nada; si no, queda el rango anterior (y la guardia de antes) para «Volver a …» bajo los filtros.
   */
  const viewRange = useCallback((from: string, to: string, templateId?: number) => {
    const current = filtersRef.current
    const newTemplate = templateId !== undefined && templateId !== current.templateId ? templateId : undefined
    if (current.from && current.to && isoDate(current.from) === from && isoDate(current.to) === to) {
      // El rango ya es ese: solo puede faltar la guardia
      if (newTemplate !== undefined) patchFilters({ templateId: newTemplate })
      return
    }
    const preset = customPeriod ? null : activePreset(current.from, current.to, today)
    let label = preset ? intl.formatMessage({ id: PERIOD_PRESET_NAME_ID[preset] }) : ''
    if (!label && current.from && current.to) {
      const withYear = periodNeedsYear(current.from, current.to, today)
      label = intl.formatMessage({ id: 'PeriodFieldRange' }, {
        from: formatShortDate(current.from, withYear),
        to: formatShortDate(current.to, withYear),
      })
    }
    setRangeOrigin({
      previousFrom: current.from,
      previousTo: current.to,
      custom: customPeriod,
      label,
      from,
      to,
      template: newTemplate === undefined ? undefined : { previous: current.templateId, applied: newTemplate },
    })
    setCustomPeriod(true)
    const next = {
      ...current,
      from: parseIsoDate(from),
      to: parseIsoDate(to),
      ...(newTemplate === undefined ? {} : { templateId: newTemplate }),
    }
    setFiltersNow(next)
    applyFilters(next, true)
  }, [customPeriod, today, intl, setFiltersNow, applyFilters, patchFilters])

  /**
   * «Volver a {período anterior}»: devuelve las fechas de antes de «Ver solo este día». Si ese paso también puso una guardia
   * y sigue siendo la elegida, vuelve la de antes; los demás filtros quedan como están.
   */
  const returnToPreviousRange = useCallback(() => {
    if (!rangeOrigin) return
    setCustomPeriod(rangeOrigin.custom)
    const origin = rangeOrigin.template
    const restoreTemplate = origin !== undefined && filtersRef.current.templateId === origin.applied
    const next = {
      ...filtersRef.current,
      from: rangeOrigin.previousFrom,
      to: rangeOrigin.previousTo,
      ...(restoreTemplate ? { templateId: origin.previous } : {}),
    }
    setFiltersNow(next)
    applyFilters(next, true)
    setRangeOrigin(null)
  }, [rangeOrigin, setFiltersNow, applyFilters])

  /** «Reintentar» después de un error. */
  const retry = useCallback(() => {
    window.clearTimeout(debounceRef.current)
    const request = toSummaryRequest(filtersRef.current)
    if (request && Object.keys(validate(filtersRef.current)).length === 0) void run(request)
  }, [run])

  /** El enlace «Volver a …» solo vale mientras se siga viendo el rango que salió de «Ver solo este día». */
  const originActive = rangeOrigin !== null && !!filters.from && !!filters.to
    && isoDate(filters.from) === rangeOrigin.from && isoDate(filters.to) === rangeOrigin.to
  useEffect(() => {
    if (rangeOrigin && !originActive) setRangeOrigin(null)
  }, [rangeOrigin, originActive])

  const period = periodChoiceOf(filters.from, filters.to, customPeriod, today)
  const rangeText = useMemo(
    () => (period === 'custom' ? '' : rangeTextOf(intl, filters.from, filters.to, today)),
    [period, filters.from, filters.to, today, intl],
  )

  return {
    filters,
    errors,
    period,
    rangeText,
    options,
    summary,
    applied,
    status,
    errorMessage,
    /** Los datos que se ven no son los de los filtros: se están consultando o el cambio espera el debounce. */
    busy: status === 'loading' || pending,
    ensureLoaded,
    changeFilter,
    patchFilters,
    changeDate,
    commitDates,
    changePeriod,
    viewRange,
    /** De dónde se vino con «Ver solo este día» (con el nombre del período anterior para el enlace); null si no aplica. */
    rangeOrigin: originActive ? rangeOrigin : null,
    returnToPreviousRange,
    retry,
  }
}

export type UrgencySummaryData = ReturnType<typeof useUrgencySummaryData>
